<template>
  <section v-if="concertStatus !== 'Verplaatst'" class="relative md:grid md:grid-cols-12 lg:max-w-screen-xl xl:max-w-screen-2xl lg:mx-auto xl:px-12 lg:gap-x-16">
    <div class="px-4 pt-8 pb-8 sm:px-12 sm:pt-12 xl:px-24 2xl:pt-12 md:col-span-10 md:col-start-2 lg:col-span-10 lg:col-start-2 2xl:col-span-10 2xl:col-start-2 bg-white">
      <div class="lg:w-[calc(100%-340px)]">
        <MoleculeEventDetailPageTitleV2 text-color="text-app-secondary-9-green--sherpa-blue" :title="$t('event-tickets')" />
        <div class="bg-white">
          <!-- 1st tab with prices -->
          <div v-if="activeTab === 'ticket_prices'" class="c-info-and-tickets-content">
            <table class="c-price-matrix w-full !text-[8px] sm:!text-sm lg:!text-base font-semibold">
              <!-- Table row with table headers containing the ranks -->
              <thead>
                <tr>
                  <th></th>
                  <th v-for="zone in matrix.zones" :key="zone" class="c-rank">
                    <span
                      class="c-rank__text"
                      :class="{
                        'border-app-price-rank-1': /1$/.test(zone),
                        'border-app-price-rank-1p': /1[+]$/.test(zone),
                        'border-app-price-rank-2': /2$/.test(zone),
                        'border-app-price-rank-3': /3$/.test(zone),
                        'border-app-price-rank-4': /4$/.test(zone),
                        'border-app-price-rank-5': /5$/.test(zone),
                        'border-app-price-rank-gold': /gold|goud$/i.test(zone),
                        'border-app-price-rank-silver': /silver|zilver$/i.test(zone),
                      }"
                      >{{ $t('event-rank', { rank: zone }) }}</span
                    >
                  </th>
                  <th class="c-empty-cell" role="presentation"></th>
                </tr>
              </thead>
              <tbody>
                <!-- Multiple table rows possible with price types and prices -->
                <tr v-for="type in matrix.types" :key="type">
                  <th>
                    <div class="c-price-type__inner" role="presentation">
                      <span class="c-price-type__text pr-1">{{ type }}</span>
                    </div>
                  </th>
                  <td v-for="zone in matrix.zones" :key="zone" class="c-price">
                    <div class="c-price__inner" role="presentation">
                      <span v-if="matrix.getPrice(zone, type)" class="c-price__text px-1">{{ $t('event-price', { price: $n(matrix.getPrice(zone, type), 'currency') }) }}</span>
                    </div>
                  </td>
                  <td class="c-empty-cell" :class="`c-empty-cell-${matrix.zones.length}`" role="presentation"></td>
                </tr>
              </tbody>
            </table>
          </div>
          <slot />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const props = defineProps({
  title: { type: String, required: false, default: undefined },
  body: { type: String, required: false, default: undefined },
  prices: { type: Array as PropType<any[]>, required: false, default: () => [] },
  room: { type: String, required: false, default: '' },
  additionalEvents: { type: Array as PropType<any[]>, required: false, default: () => [] },
  concertStatus: { type: String, required: false, default: '' },
})

const activeTab = ref('ticket_prices')
const { matrix } = setupTixPricing(props.prices)
const hasMatrixPricing = unref(matrix)?.zones.length > 0 || false

// show tab "other_data" if matrix is empty
if (props.additionalEvents.length > 0 && hasMatrixPricing === false) {
  activeTab.value = 'other_data'
}

// cssProps() {
//   return {
//     '--th-width': 90 / (this.matrix.zones.length + 1) + '%',
//   }
// },
</script>

<style lang="scss" scoped>
a#tab-other-data {
  top: -250px;
}
.c-info-and-tickets-title {
  @apply relative flex-col bg-white rounded-t-lg px-4 pt-3 pb-2 font-semibold text-sm leading-none opacity-50 transform transition duration-200 cursor-pointer;

  @screen md {
    @apply px-3;
  }

  @screen xl {
    @apply px-5;
  }

  &:hover {
    @apply opacity-100;
  }

  &.c-info-and-tickets-title--active {
    @apply opacity-100;

    &::after {
      @apply h-1 bg-app-primary-1-orange--bittersweet absolute bottom-0 left-0 ml-4 -mb-1;
      content: '';
      width: calc(100% - theme('spacing.4') - theme('spacing.4'));

      @screen md {
        @apply ml-3;
        width: calc(100% - theme('spacing.3') - theme('spacing.3'));
      }

      @screen xl {
        @apply ml-5;
        width: calc(100% - theme('spacing.5') - theme('spacing.5'));
      }
    }
  }
}

.c-info-and-tickets-content {
  @apply py-10 text-sm;
}

.c-price {
  @apply border-solid border-l border-app-primary-grey--concrete;
}

.c-price-matrix {
  font-size: 10px;

  @screen sm {
    @apply text-xs; // small size instead of text-sm in the design for when the matrix has 5 columns and long words like Vriendenloterij
  }

  thead tr:first-child {
    th {
      @apply border-solid border-app-primary-grey--concrete;

      @screen lg {
        @apply border-l;
      }

      &:first-child {
        @apply border-l-0;
      }
      &:last-child {
        @apply border-l-0;
      }

      &:not(:first-child):not(:last-child) {
        @apply w-[var(--th-width)];
      }
    }
  }

  .c-rank {
    padding-bottom: 5px;

    @screen sm {
      padding-bottom: 8px;
    }
  }

  .c-rank__text {
    @apply pb-1 border-solid border-b-4 text-left inline-block w-full px-1;

    @screen lg {
      @apply w-[90%];
    }
  }

  .c-price-type__text {
    @apply w-20 text-left align-middle;

    @screen md {
      @apply w-32;
    }
  }

  .c-empty-cell {
    // 1 price types
    &.c-empty-cell-1 {
      @apply w-4/12;

      @screen sm {
        @apply w-6/12;
      }

      @screen md {
        @apply w-7/12;
      }

      @screen 2xl {
        @apply w-8/12;
      }
    }

    // 2 price types
    &.c-empty-cell-2 {
      @apply hidden w-6/12;

      @screen md {
        @apply table-cell;
      }
    }

    // 3 price types
    &.c-empty-cell-3 {
      @apply hidden w-4/12;

      @screen md {
        @apply table-cell;
      }
    }

    // 4 price types
    &.c-empty-cell-4 {
      @apply hidden w-2/12;

      @screen md {
        @apply table-cell;
      }
    }

    // 5 price types
    &.c-empty-cell-5 {
      @apply hidden w-1/12;

      @screen md {
        @apply table-cell;
      }
    }
  }

  .c-price-type__inner {
    @apply table-cell;
  }

  td,
  th {
    @apply text-center;
  }

  .c-price__inner {
    @apply flex justify-center w-full self-stretch;
  }

  .c-price-type__text {
    @apply table-cell w-full leading-none;

    @screen md {
      @apply h-16;
    }
  }

  .c-price__text {
    @apply flex items-center justify-start text-left py-3 font-normal w-full;

    @screen md {
      @apply h-16 w-[90%];
    }
  }

  tbody tr:nth-child(1) td .c-price__inner,
  tbody tr:nth-child(1) th .c-price__inner,
  tbody tr:nth-child(1) th .c-price-type__inner {
    @apply border-solid border-white;
    border-top-width: 12px;
  }
}
</style>
